// src/components/Booklet/BookletList.js
import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Plus, 
  Eye, 
  Edit, 
  Share, 
  MoreVertical,
  Search,
  Filter,
  Grid,
  List,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookletService } from '../../services/bookletService';
import toast from 'react-hot-toast';

const BookletList = () => {
  const { user } = useAuth();
  const [booklets, setBooklets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [showActions, setShowActions] = useState(null);

  useEffect(() => {
    loadBooklets();
  }, [user]);

  const loadBooklets = async () => {
    try {
      setLoading(true);
      const userBooklets = await bookletService.getUserBooklets(user.uid);
      setBooklets(userBooklets);
    } catch (error) {
      toast.error('Error loading booklets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBooklet = async (booklet) => {
    if (window.confirm(`Are you sure you want to delete "${booklet.title}"?`)) {
      try {
        await bookletService.deleteBooklet(booklet.id, user.uid);
        toast.success('Booklet deleted successfully');
        loadBooklets();
      } catch (error) {
        toast.error('Error deleting booklet');
        console.error(error);
      }
    }
  };

  const shareBooklet = async (booklet) => {
    const url = `${window.location.origin}/booklet/${booklet.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: booklet.title,
          text: booklet.description,
          url: url
        });
      } catch (error) {
        navigator.clipboard.writeText(url);
        toast.success('Booklet link copied to clipboard');
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Booklet link copied to clipboard');
    }
  };

  const filteredBooklets = booklets
    .filter(booklet => 
      booklet.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (filterStatus === 'all' || booklet.status === filterStatus)
    );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Booklets</h2>
          <p className="text-gray-600">
            {booklets.length} booklet{booklets.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <Link
          to="/booklet/create"
          className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Booklet
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search booklets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Booklets Display */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading booklets...</p>
          </div>
        ) : filteredBooklets.length === 0 ? (
          <div className="p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'No booklets found matching your search.' : 'No booklets created yet.'}
            </p>
            {!searchTerm && (
              <Link
                to="/booklet/create"
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Create Your First Booklet
              </Link>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBooklets.map((booklet) => (
              <div key={booklet.id} className="group relative bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                {/* Booklet Preview */}
                <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-lg flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-gray-400" />
                </div>

                {/* Actions Menu */}
                <div className="absolute top-3 right-3">
                  <div className="relative">
                    <button
                      onClick={() => setShowActions(showActions === booklet.id ? null : booklet.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow-sm"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {showActions === booklet.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <Link
                          to={`/booklet/${booklet.id}`}
                          onClick={() => setShowActions(null)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Link>
                        <Link
                          to={`/booklet/${booklet.id}/edit`}
                          onClick={() => setShowActions(null)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            setShowActions(null);
                            shareBooklet(booklet);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Share className="w-4 h-4 mr-2" />
                          Share
                        </button>
                        <button
                          onClick={() => {
                            setShowActions(null);
                            deleteBooklet(booklet);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booklet.status)}`}>
                    {booklet.status}
                  </span>
                </div>

                {/* Booklet Info */}
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 text-sm mb-1 truncate" title={booklet.title}>
                    {booklet.title}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                    {booklet.description || 'No description'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{booklet.totalPages} pages</span>
                    <span>{formatDate(booklet.updatedAt)}</span>
                  </div>
                </div>

                {/* Click to view */}
                <Link 
                  to={`/booklet/${booklet.id}`}
                  className="absolute inset-0 z-0"
                />
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBooklets.map((booklet) => (
                  <tr key={booklet.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BookOpen className="w-5 h-5 text-indigo-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{booklet.title}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {booklet.description || 'No description'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(booklet.status)}`}>
                        {booklet.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booklet.totalPages}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booklet.analytics?.views || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(booklet.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/booklet/${booklet.id}`}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                          title="View Booklet"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/booklet/${booklet.id}/edit`}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Edit Booklet"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => shareBooklet(booklet)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Share Booklet"
                        >
                          <Share className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteBooklet(booklet)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Delete Booklet"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Click outside to close actions menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowActions(null)}
        />
      )}
    </div>
  );
};

export default BookletList;